/**
 * Solio Product Hero — purchase module.
 *
 * Ports the logic of docs/kaufmodul-prototyp.html into the live PDP:
 *   01 Menge      — variant chips (per-bottle price for the active mode),
 *                   supply readout, threshold-driven free-shipping nudge.
 *   02 Lieferung  — subscription vs one-time cards (real radios), the selected
 *                   card expands, frequency picker, supply-vs-interval fit note.
 *   03 Auswahl    — line-item summary, mode-aware CTA, recurring commitment line.
 *
 * Pricing is never computed from a bundle formula: variant.price and the
 * selling-plan allocation price are read from data-* attributes rendered by the
 * section (raw cents). Shipping is display-only — it mirrors the delivery-zone
 * rates from section settings, evaluated on the line total AFTER the plan
 * discount, and scoped to the customer's market. Config + all copy come from the
 * two JSON <script> blocks the section emits (nothing user-facing is hardcoded).
 */
(function () {
  'use strict';

  var roots = document.querySelectorAll('[data-solio-product-hero]');
  if (!roots.length) return;

  var CHECK = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2 7.4 5.4 11 12 3.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ARROW = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M7 2.2v9.6M2.6 7.4 7 11.8l4.4-4.4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  roots.forEach(function (root) { init(root); });

  function parseJSON(el) { try { return JSON.parse(el.textContent); } catch (e) { return null; } }

  function init(root) {
    var CFG = (function () { var el = root.querySelector('[data-km-config]'); return el ? parseJSON(el) : null; })() || {};
    var T   = (function () { var el = root.querySelector('[data-km-i18n]');   return el ? parseJSON(el) : null; })() || {};

    /* ---------- money ---------- */
    var MONEY_FMT = CFG.moneyFormat || '€{{amount_with_comma_separator}}';
    function money(cents) {
      var v = (cents || 0) / 100;
      var withComma = v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      var noDec = Math.round(v).toLocaleString('de-DE');
      return MONEY_FMT
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(v)))
        .replace(/\{\{\s*amount\s*\}\}/g, v.toFixed(2));
    }
    function tmpl(str, vars) {
      return (str || '').replace(/\{(\w+)\}/g, function (_, k) { return vars[k] != null ? vars[k] : ''; });
    }
    /* Bold the lead clause (everything before the em dash), matching the prototype. */
    function boldLead(str) {
      var i = str.indexOf(' — ');
      return i < 0 ? str : '<b>' + str.slice(0, i) + '</b>' + str.slice(i);
    }
    /* Bold the lead clause up to the first colon ("Flexibel: …"). */
    function boldColon(str) {
      var i = str.indexOf(': ');
      return i < 0 ? str : '<b>' + str.slice(0, i + 1) + '</b>' + str.slice(i + 1);
    }
    function perkLi(html, muted) { return '<span class="solio-product-hero__perk-ic" aria-hidden="true">' + CHECK + '</span><span>' + html + '</span>'; }
    function set(sel, text) { var el = root.querySelector(sel); if (el) el.textContent = text; }
    function html(sel, markup) { var el = root.querySelector(sel); if (el) el.innerHTML = markup; }

    /* ---------- shipping (market-scoped, display only) ---------- */
    var country = CFG.country || '';
    var shipCfg = (CFG.shipping && CFG.shipping[country]) || null;   /* null => neutral market */
    var hasFreeTier = !!(shipCfg && shipCfg.free != null);
    function shipFor(goods) {
      if (!shipCfg) return null;                                     /* unknown → compute nothing */
      if (shipCfg.free != null && goods >= shipCfg.free) return 0;
      return shipCfg.rate;
    }

    /* ---------- data hooks ---------- */
    var variantInput     = root.querySelector('[data-variant-id]');
    var sellingPlanInput = root.querySelector('[data-selling-plan-input]');
    var variantPills     = Array.prototype.slice.call(root.querySelectorAll('[data-variant-pill]'));
    var optCards         = Array.prototype.slice.call(root.querySelectorAll('[data-opt]'));
    var freqBtns         = Array.prototype.slice.call(root.querySelectorAll('[data-fbtn]'));
    var pillGroup        = root.querySelector('[data-variant-group]');
    var perSuffix        = pillGroup ? (pillGroup.dataset.perSuffix || '') : '';

    var DAYS    = CFG.daysPerBottle || 25;
    var PERCENT = CFG.percent || 0;
    var buyMode = !!CFG.buyMode;

    /* variant model, from the chips (all money in cents) */
    var variants = variantPills.map(function (p) {
      return {
        el: p,
        id: p.dataset.variantId,
        qty: parseInt(p.dataset.variantQty, 10) || 1,
        base: parseInt(p.dataset.variantPrice, 10) || 0,
        sub: p.dataset.variantSubPrice ? parseInt(p.dataset.variantSubPrice, 10) : null,
        available: p.dataset.variantAvailable === 'true'
      };
    });
    function byId(id) { for (var i = 0; i < variants.length; i++) if (variants[i].id === id) return variants[i]; return null; }
    function byQty(q) { for (var i = 0; i < variants.length; i++) if (variants[i].qty === q) return variants[i]; return null; }

    /* months → selling-plan id, derived from the frequency controls */
    var monthsToPlan = {};
    var planMonths = [];
    freqBtns.forEach(function (b) {
      var m = parseInt(b.dataset.months, 10);
      monthsToPlan[m] = b.dataset.planId;
      if (planMonths.indexOf(m) < 0) planMonths.push(m);
    });
    planMonths.sort(function (a, b) { return a - b; });
    var hasPlans = freqBtns.length > 0;

    /* ---------- state ---------- */
    var startPill = variantPills.filter(function (p) { return p.classList.contains('is-selected'); })[0] || variantPills[0];
    var state = {
      variant: startPill ? byId(startPill.dataset.variantId) : variants[0],
      mode: hasPlans && CFG.defaultMode !== 'once' ? 'abo' : 'once',   /* default from Liquid default_purchase_type; ?selling_plan= still wins */
      months: CFG.defaultMonths || planMonths[0] || 1
    };
    if (!state.variant) return;

    /* URL params take precedence on load (?variant=, ?selling_plan=) */
    try {
      var params = new URL(window.location.href).searchParams;
      var vp = params.get('variant');
      if (vp && byId(vp)) state.variant = byId(vp);
      var sp = params.get('selling_plan');
      if (sp && hasPlans) {
        Object.keys(monthsToPlan).forEach(function (m) {
          if (String(monthsToPlan[m]) === String(sp)) { state.mode = 'abo'; state.months = parseInt(m, 10); }
        });
      }
    } catch (e) {}

    /* ---------- localized grammar ---------- */
    function bottles(n) { return n === 1 ? T.bottle_one : tmpl(T.bottle_other, { n: n }); }
    function lasts(n)   { return n === 1 ? T.lasts_one : T.lasts_other; }
    function cadence(m) { return m === 1 ? T.cadence_month : tmpl(T.cadence_every_n, { n: m }); }
    function cadenceInline(m) { return m === 1 ? T.cadence_inline_month : tmpl(T.cadence_inline_every_n, { n: m }); }

    /* cadence whose interval best matches the amount ordered */
    function recommended(qty) {
      if (!planMonths.length) return 1;
      var days = qty * DAYS, best = planMonths[0], diff = Infinity;
      planMonths.forEach(function (m) { var d = Math.abs(days - m * 30); if (d < diff) { diff = d; best = m; } });
      return best;
    }

    /* ---------- calc (all cents) ---------- */
    function calc() {
      var v = state.variant, qty = v.qty;
      var subtotal = v.base;                              /* one-time goods */
      var subCents = v.sub != null ? v.sub : v.base;      /* subscription goods */
      var discount = Math.max(0, subtotal - subCents);
      var goods = state.mode === 'abo' ? subCents : subtotal;
      var aboShip = shipFor(subCents), onceShip = shipFor(subtotal);
      return {
        qty: qty,
        perOnce: Math.round(subtotal / qty),
        perAbo: Math.round(subCents / qty),
        subtotal: subtotal, subCents: subCents, discount: discount,
        goods: goods, ship: shipFor(goods), total: goods + (shipFor(goods) || 0),
        aboShip: aboShip, aboTotal: subCents + (aboShip || 0),
        onceShip: onceShip, onceTotal: subtotal + (onceShip || 0),
        toFree: hasFreeTier ? Math.max(0, shipCfg.free - goods) : 0,
        days: qty * DAYS
      };
    }

    /* ---------- render ---------- */
    function render() {
      var c = calc();
      var isAbo = state.mode === 'abo';
      var rec = recommended(state.variant.qty);

      /* 01 — chips: selection + per-bottle for the active mode */
      variantPills.forEach(function (p) {
        var v = byId(p.dataset.variantId);
        var sel = v.id === state.variant.id;
        p.classList.toggle('is-selected', sel);
        p.setAttribute('aria-checked', sel ? 'true' : 'false');
        p.tabIndex = sel ? 0 : -1;
        var per = (isAbo && v.sub != null) ? Math.round(v.sub / v.qty) : Math.round(v.base / v.qty);
        var pe = p.querySelector('[data-pack-each]');
        if (pe) pe.textContent = tmpl(T.pack_each, { price: money(per) });
        /* saving per bottle vs the one-off single-bottle price */
        var one = byQty(1), ps = p.querySelector('[data-pack-save]');
        if (ps) {
          var pct = one ? Math.round((1 - per / one.base) * 100) : 0;
          ps.textContent = pct > 0 ? tmpl(T.pack_save, { percent: pct }) : '';
        }
      });

      /* supply */
      html('[data-supply]', tmpl(T.supply, { bottles: '<b>' + bottles(c.qty) + '</b>', lasts: lasts(c.qty), days: c.days }));

      /* nudge — driven by the threshold, not the bottle count */
      var nudge = root.querySelector('[data-nudge]');
      if (nudge) {
        var two = byQty(2);
        var showNudge = hasFreeTier && c.toFree > 0 && state.variant.qty < 2 && two;
        if (showNudge) {
          var twoGoods = (isAbo && two.sub != null) ? two.sub : two.base;
          var twoTotal = twoGoods + (shipFor(twoGoods) || 0);
          nudge.hidden = false;
          nudge.innerHTML = '<span class="solio-product-hero__nudge-ic" aria-hidden="true">' + ARROW + '</span><span>' +
            tmpl(T.nudge, {
              gap: '<b>' + money(c.toFree) + '</b>',
              two: '<b>' + T.nudge_two + '</b>',
              delta: money(Math.max(0, twoTotal - c.total)),
              full: money(c.total),
              'switch': '<u>' + T.nudge_switch + '</u>'
            }) + '</span>';
        } else {
          nudge.hidden = true;
        }
      }

      /* 02 — cards on/off + radios */
      optCards.forEach(function (card) {
        var on = card.dataset.mode === state.mode;
        card.classList.toggle('is-on', on);
        var r = card.querySelector('[data-mode-radio]');
        if (r) r.checked = on;
      });

      /* subscription card */
      set('[data-save]', tmpl(T.opt_save, { percent: PERCENT }));
      set('[data-abo-was]', money(c.onceTotal));
      set('[data-abo-now]', money(c.aboTotal));
      set('[data-abo-each]', (c.aboShip === 0 || c.aboShip == null)
        ? tmpl(T.each_per_bottle, { price: money(c.perAbo) })
        : tmpl(T.each_plus_ship, { price: money(c.perAbo), ship: money(c.aboShip) }));
      set('[data-abo-sub]', c.aboShip === 0 ? tmpl(T.opt_abo_sub_free, { cadence: cadence(state.months) })
        : c.aboShip == null ? tmpl(T.opt_abo_sub_neutral, { cadence: cadence(state.months) })
        : tmpl(T.opt_abo_sub_ship, { cadence: cadence(state.months) }));

      /* one-time card */
      set('[data-once-now]', money(c.onceTotal));
      set('[data-once-each]', (c.onceShip === 0 || c.onceShip == null)
        ? tmpl(T.each_per_bottle, { price: money(c.perOnce) })
        : tmpl(T.each_plus_ship, { price: money(c.perOnce), ship: money(c.onceShip) }));
      set('[data-once-sub]', c.onceShip === 0 ? T.opt_once_sub_free
        : c.onceShip == null ? T.opt_once_sub_neutral
        : tmpl(T.opt_once_sub_ship, { ship: money(shipCfg.rate) }));

      /* panel: headline, goods price (strike = one-off price on Abo), points, interval */
      var panel = root.querySelector('[data-panel]');
      if (panel) panel.classList.toggle('is-abo', isAbo);
      set('[data-panel-title]', isAbo ? T.panel_title_abo : T.panel_title_once);
      set('[data-panel-now]', money(c.goods));
      set('[data-panel-was]', isAbo && c.discount > 0 ? money(c.subtotal) : '');
      var shipTxt = c.ship === 0 ? T.ship_free : c.ship == null ? T.ship_neutral : tmpl(T.ship_fee, { ship: money(c.ship) });
      var pts = isAbo
        ? [boldColon(tmpl(T.point_abo_1, { percent: PERCENT })), boldColon(T.point_abo_2), shipTxt]
        : [boldColon(T.point_once_1), shipTxt];
      html('[data-panel-points]', pts.map(function (t) { return '<li>' + perkLi(t) + '</li>'; }).join(''));
      var fw = root.querySelector('[data-freq-wrap]');
      if (fw) fw.hidden = !isAbo;
      var fs = root.querySelector('[data-freq-select]');
      if (fs) fs.value = String(state.months);
      Array.prototype.forEach.call(root.querySelectorAll('[data-abo-only]'), function (el) { el.hidden = !isAbo; });

      /* subscription shipping perk */
      var shipLi = root.querySelector('[data-perk-ship]');
      if (shipLi) {
        shipLi.className = '';
        if (c.aboShip === 0) {
          html('[data-perk-ship]', perkLi(boldLead(T.perk_ship_free)));
        } else if (c.aboShip == null) {
          shipLi.className = 'solio-product-hero__perk--muted';
          html('[data-perk-ship]', perkLi(T.perk_ship_neutral));
        } else if (hasFreeTier) {
          shipLi.className = 'solio-product-hero__perk--muted';
          html('[data-perk-ship]', perkLi(boldLead(tmpl(T.perk_ship_fee, { ship: money(c.aboShip), threshold: money(shipCfg.free) }))));
        } else {
          shipLi.className = 'solio-product-hero__perk--muted';
          html('[data-perk-ship]', perkLi(boldLead(tmpl(T.perk_ship_flat, { ship: money(c.aboShip) }))));
        }
      }

      /* one-time shipping note */
      var onceShipLi = root.querySelector('[data-once-ship]');
      if (onceShipLi) {
        if (c.onceShip === 0) {
          html('[data-once-ship]', perkLi(boldLead(tmpl(T.perk_once_ship_free, { threshold: money(shipCfg.free) }))));
        } else if (c.onceShip == null) {
          html('[data-once-ship]', perkLi(T.perk_once_ship_neutral));
        } else if (hasFreeTier) {
          html('[data-once-ship]', perkLi(boldLead(tmpl(T.perk_once_ship_fee, { ship: money(c.onceShip), threshold: money(shipCfg.free) }))));
        } else {
          html('[data-once-ship]', perkLi(boldLead(tmpl(T.perk_once_ship_flat, { ship: money(c.onceShip) }))));
        }
      }

      /* frequency picker */
      freqBtns.forEach(function (b) {
        var m = parseInt(b.dataset.months, 10);
        var on = m === state.months;
        b.classList.toggle('is-on', on);
        var r = b.querySelector('input');
        if (r) r.checked = on;
        var note = b.querySelector('[data-fbtn-note]');
        if (note) note.textContent = (m === rec && Math.abs(state.variant.qty * DAYS - m * 30) <= 12) ? T.freq_fits : '';
      });

      /* supply-vs-interval fit note (warn only when they would run out) */
      var head = tmpl(T.fit_head, { bottles: bottles(c.qty), lasts: lasts(c.qty), days: c.days });
      var covered = state.months * 30, warn = false, fit;
      if (Math.abs(c.days - covered) <= 12) fit = tmpl(T.fit_exact, { head: head });
      else if (c.days < covered) { warn = true; fit = tmpl(T.fit_short, { head: head, gap: covered - c.days }); }
      else fit = tmpl(T.fit_surplus, { head: head, extra: c.days - covered });
      var fitnote = root.querySelector('[data-fitnote]');
      if (fitnote) fitnote.classList.toggle('is-warn', warn);
      set('[data-fit-text]', fit);

      /* 03 — summary */
      set('[data-sum-items]', tmpl(T.sum_items, { n: c.qty, each: money(c.perOnce) }));
      set('[data-sum-subtotal]', money(c.subtotal));
      var discRow = root.querySelector('[data-sum-discount-row]');
      if (discRow) discRow.hidden = !(isAbo && c.discount > 0);
      set('[data-sum-discount-label]', tmpl(T.sum_discount_label, { percent: PERCENT }));
      set('[data-sum-discount]', tmpl(T.sum_discount_value, { amount: money(c.discount) }));
      var shipRow = root.querySelector('[data-sum-ship-row]');
      set('[data-sum-ship-label]', T.sum_ship_label);
      if (c.ship == null) { set('[data-sum-ship]', T.sum_ship_neutral); if (shipRow) shipRow.classList.remove('is-free'); }
      else if (c.ship === 0) { set('[data-sum-ship]', T.sum_ship_free); if (shipRow) shipRow.classList.add('is-free'); }
      else { set('[data-sum-ship]', money(c.ship)); if (shipRow) shipRow.classList.remove('is-free'); }
      set('[data-sum-label]', isAbo ? cadence(state.months) : T.sum_label_once);
      set('[data-sum-tax]', T.sum_tax);
      set('[data-sum-total]', money(c.total));

      /* CTA + commitment line (buy mode only; preview keeps the waitlist CTA) */
      if (buyMode) {
        var atcLabel = root.querySelector('[data-atc-label]');
        if (atcLabel) atcLabel.textContent = isAbo ? tmpl(T.cta_abo, { total: money(c.total) }) : tmpl(T.cta_once, { total: money(c.total) });
        var commit = root.querySelector('[data-commit]');
        if (commit) {
          var saved = c.onceTotal - c.aboTotal;
          var shipPhrase = c.ship === 0 ? T.ship_phrase_free : c.ship == null ? T.ship_phrase_neutral : tmpl(T.ship_phrase_fee, { ship: money(c.ship) });
          commit.innerHTML = isAbo
            ? tmpl(T.commit_abo, { cadence: '<b>' + cadenceInline(state.months) + '</b>', total: money(c.aboTotal), ship: shipPhrase, saved: money(saved) })
            : tmpl(T.commit_once, { total: money(c.onceTotal), ship: shipPhrase, abo: money(c.aboTotal), saved: money(saved) });
        }
      }

      /* add-to-cart inputs — omit selling_plan entirely for one-time */
      if (variantInput) variantInput.value = state.variant.id;
      if (sellingPlanInput) {
        if (isAbo && hasPlans && monthsToPlan[state.months]) {
          sellingPlanInput.disabled = false;
          sellingPlanInput.value = monthsToPlan[state.months];
        } else {
          sellingPlanInput.value = '';
          sellingPlanInput.disabled = true;
        }
      }

      /* availability on the CTA + sticky */
      var atc = root.querySelector('[data-atc]');
      if (atc) { atc.disabled = !state.variant.available; if (state.variant.available) atc.removeAttribute('aria-disabled'); else atc.setAttribute('aria-disabled', 'true'); }
      var stickyPrice = root.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = money(c.total);
      var stickyVariant = root.querySelector('[data-sticky-variant]');
      if (stickyVariant) stickyVariant.textContent = state.variant.el.dataset.variantTitle || '';
      var stickyCta = root.querySelector('[data-sticky-cta]');
      if (stickyCta && stickyCta.type === 'submit') {
        stickyCta.disabled = !state.variant.available;
        if (state.variant.available) stickyCta.removeAttribute('aria-disabled'); else stickyCta.setAttribute('aria-disabled', 'true');
      }

      /* keep the URL shareable/restorable */
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', state.variant.id);
        if (isAbo && hasPlans && monthsToPlan[state.months]) url.searchParams.set('selling_plan', monthsToPlan[state.months]);
        else url.searchParams.delete('selling_plan');
        window.history.replaceState({}, '', url);
      } catch (e) {}
    }

    /* ---------- interaction ---------- */
    variantPills.forEach(function (p) {
      if (p.getAttribute('aria-disabled') === 'true') return;
      p.addEventListener('click', function () {
        var v = byId(p.dataset.variantId);
        if (!v) return;
        state.variant = v;
        if (hasPlans) state.months = recommended(v.qty);
        render();
      });
    });

    /* roving arrow keys within the quantity group */
    if (pillGroup) {
      pillGroup.addEventListener('keydown', function (e) {
        if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].indexOf(e.key) < 0) return;
        e.preventDefault();
        var enabled = variantPills.filter(function (p) { return p.getAttribute('aria-disabled') !== 'true'; });
        var idx = enabled.indexOf(state.variant.el);
        if (idx < 0) return;
        var dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
        var next = enabled[(idx + dir + enabled.length) % enabled.length];
        if (next) {
          state.variant = byId(next.dataset.variantId);
          if (hasPlans) state.months = recommended(state.variant.qty);
          render();
          next.focus();
        }
      });
    }

    var nudgeBtn = root.querySelector('[data-nudge]');
    if (nudgeBtn) {
      nudgeBtn.addEventListener('click', function () {
        var two = byQty(2);
        if (!two) return;
        state.variant = two;
        if (hasPlans) state.months = recommended(2);
        render();
      });
    }

    optCards.forEach(function (card) {
      card.addEventListener('click', function (e) {
        /* Clicks inside the frequency picker are owned by the freq radios —
           handling them here too would re-render with a stale state.months and
           snap the highlight back to the old button. */
        if (e.target.closest('[data-freq-group]')) return;
        state.mode = card.dataset.mode;
        render();
      });
      var r = card.querySelector('[data-mode-radio]');
      if (r) r.addEventListener('change', function () { state.mode = card.dataset.mode; render(); });
    });

    var freqSelect = root.querySelector('[data-freq-select]');
    if (freqSelect) {
      freqSelect.addEventListener('change', function () {
        var m = parseInt(freqSelect.value, 10);
        if (monthsToPlan[m]) { state.months = m; state.mode = 'abo'; render(); }
      });
    }

    freqBtns.forEach(function (b) {
      var r = b.querySelector('input');
      if (r) r.addEventListener('change', function () { state.months = parseInt(b.dataset.months, 10); state.mode = 'abo'; render(); });
    });

    /* ---------- gallery: thumb rail + tap-to-advance ---------- */
    var thumbButtons = root.querySelectorAll('[data-thumb-btn]');
    var mediaItems   = root.querySelectorAll('.solio-product-hero__media');
    var mediaAdvance = root.querySelector('[data-media-advance]');
    var activeIndex  = 0;

    function setActiveSlide(idx) {
      activeIndex = idx;
      mediaItems.forEach(function (li, i) { li.setAttribute('data-active', i === idx ? 'true' : 'false'); });
      thumbButtons.forEach(function (b, i) {
        b.classList.toggle('is-active', i === idx);
        if (i === idx && typeof b.scrollIntoView === 'function') b.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      });
    }
    if (mediaItems.length) setActiveSlide(0);
    thumbButtons.forEach(function (btn) {
      btn.addEventListener('click', function () { setActiveSlide(parseInt(btn.dataset.thumbIndex, 10) || 0); });
    });
    if (mediaAdvance && mediaItems.length > 1) {
      mediaAdvance.addEventListener('click', function () { setActiveSlide((activeIndex + 1) % mediaItems.length); });
    }

    /* ---------- sticky bar visibility ---------- */
    var stickyBar = root.querySelector('[data-sticky-bar]');
    if (stickyBar) {
      var watchTarget = root.querySelector('[data-atc]') || root.querySelector('[data-waitlist-trigger]');
      if (watchTarget && 'IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            stickyBar.classList.toggle('is-visible', show);
            stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
            if (show) stickyBar.removeAttribute('hidden');
          });
        }, { rootMargin: '0px 0px -40px 0px', threshold: 0 });
        io.observe(watchTarget);
      } else {
        var onScroll = function () {
          var show = window.scrollY > 600;
          stickyBar.classList.toggle('is-visible', show);
          if (show) stickyBar.removeAttribute('hidden');
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
    }

    render();
  }
})();
